import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { FavoriteBorder, ChatBubbleOutline, ShareOutlined, MoreHoriz, CheckCircle } from "@mui/icons-material";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  caption?: string;
  pageName?: string;
  avatarUrl?: string;
  verified?: boolean;
}

export default function FacebookReelsPreview({
  media = [],
  caption,
  pageName = "Your Page",
  avatarUrl,
  verified = false
}: Props) {
  const first = media[0];
  const isVideo = first ? getMediaKind(first) === "video" : false;

  return (
    <Box sx={styles.root}>
      {first ? (
        <Box
          component={isVideo ? "video" : "img"}
          src={first}
          autoPlay={isVideo ? true : undefined}
          muted={isVideo ? true : undefined}
          loop={isVideo ? true : undefined}
          playsInline={isVideo ? true : undefined}
          sx={styles.media}
        />
      ) : (
        <Box sx={styles.noMedia}>
          <Typography variant="body2">No media for Reels</Typography>
        </Box>
      )}

      <Box sx={styles.topGradient} />
      <Box sx={styles.bottomGradient} />

      <Box sx={styles.actions}>
        <IconButton size="small" sx={styles.actionBtn}>
          <FavoriteBorder fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.actionBtn}>
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.actionBtn}>
          <ShareOutlined fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.actionBtn}>
          <MoreHoriz fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.overlay}>
        <Box sx={styles.pageRow}>
          <Avatar
            src={avatarUrl}
            sx={styles.avatar}
          >
            {pageName?.[0] ?? "P"}
          </Avatar>
          <Typography sx={styles.pageName}>
            {pageName}
            {verified && <CheckCircle fontSize="inherit" />}
          </Typography>
        </Box>
        {caption ? (
          <Typography sx={styles.caption}>
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "9 / 16",
    bgcolor: "black",
    borderRadius: 2,
    overflow: "hidden",
    color: "common.white",
    boxShadow: 1
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    backgroundColor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.500"
  },
  topGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 140,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)"
  },
  bottomGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 160,
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)"
  },
  actions: {
    position: "absolute",
    right: 8,
    bottom: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1
  },
  actionBtn: {
    color: "common.white",
    bgcolor: "rgba(0,0,0,0.35)",
    width: 40,
    height: 40,
    borderRadius: 12,
    "&:hover": { bgcolor: "rgba(0,0,0,0.55)" }
  },
  overlay: {
    position: "absolute",
    left: 12,
    right: 80,
    bottom: 12
  },
  pageRow: {
    display: "flex",
    alignItems: "center",
    gap: 1
  },
  avatar: {
    width: 28,
    height: 28,
    bgcolor: "#1976d2",
    fontWeight: 700
  },
  pageName: {
    variant: "subtitle2",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 0.5
  },
  caption: {
    mt: 0.75,
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical"
  }
};
